// src/components/phase05/incidentReports/IncidentReportPrint.jsx
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchIncidentReport } from "../../../store/slices/incidentReportsSlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function IncidentReportPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentIncidentReport, isLoading, error } = useSelector(
    (state) => state.incidentReports || {}
  );

  useEffect(() => {
    if (id) dispatch(fetchIncidentReport(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentIncidentReport) {
      const timer = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(timer);
        window.onafterprint = null;
      };
    }
  }, [currentIncidentReport, navigate]);

  if (isLoading)
    return <div className="p-8 text-center">Loading Incident Report...</div>;
  if (error)
    return <div className="p-8 text-danger text-center">{error}</div>;
  if (!currentIncidentReport)
    return <div className="p-8 text-center">No record found.</div>;

  const d = currentIncidentReport;

  const fmtDate = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "";

  const fmtDateTime = (dt) =>
    dt
      ? new Date(dt)
        .toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
        .replace(",", "")
      : "";

  const natureOptions = [
    "Injury to client, staff, or visitor",
    "Seizure",
    "Client act of violence (self/others)",
    "Equipment failure",
    "Damage/destruction of property",
    "Client abuse or neglect",
    "Medication error",
    "Suicide or attempted suicide",
    "Exposure to communicable disease",
    "Client(s) leaving program without permission",
    "Violation of infection control policies",
    "Vehicular accident",
    "Violence or aggression",
    "Hold-up",
    "Use/possession of weapon(s)",
    "Theft - medication/missing medication",
    "Infection",
    "Falls",
    "Theft – property",
    "Harassment",
    "Other",
  ];

  return (
    <>
      <style jsx global>{`
        * {
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          font-family: Arial, sans-serif !important;
        }

        body {
          background: #fff !important;
          color: #000 !important;
          font-size: 11px !important;
        }

        .print-container {
          padding: 30px !important;
          position: relative !important;
        }

        .logo {
          position: absolute !important;
          top: 15px !important;
          left: 15px !important;
        }

        .logo img {
          height: 60px !important;
        }

        .header {
          text-align: center !important;
          margin-bottom: 20px !important;
          border-bottom: 2px solid #000 !important;
          padding-bottom: 10px !important;
        }

        .header h3 {
          font-size: 1.5rem !important;
          font-weight: bold !important;
        }

        .header h6 {
          font-size: 1rem !important;
          text-decoration: underline !important;
        }

        .section-title {
          background: #eee !important;
          padding: 4px 8px !important;
          font-weight: bold !important;
          margin: 15px 0 8px 0 !important;
          border: 1px solid #000 !important;
          text-transform: uppercase !important;
          display: block !important;
        }

        .info-table,
        .member-table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-bottom: 10px !important;
        }

        .info-table td,
        .member-table td,
        .member-table th {
          border: 1px solid #000 !important;
          padding: 6px !important;
          vertical-align: top !important;
        }

        .member-table th {
          background: #f2f2f2 !important;
          text-align: center !important;
        }

        .label {
          font-weight: bold !important;
          width: 25% !important;
          background: #f9f9f9 !important;
        }

        .text-box {
          border: 1px solid #000 !important;
          padding: 8px !important;
          min-height: 50px !important;
          margin-top: 5px !important;
        }

        .footer {
          margin-top: 30px !important;
          border-top: 1px solid #000 !important;
          padding-top: 8px !important;
          text-align: center !important;
          font-size: 10px !important;
        }
      `}</style>

      <div className="print-container">
        <div className="logo">
          <img src={logo} alt="Logo" />
        </div>

        <div className="header">
          <h3>THE RECOVERY HOUSE</h3>
          <h6>INCIDENT REPORT FORM</h6>
        </div>

        {/* Report Information */}
        <span className="section-title">Report Information</span>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Report Completed By</td>
              <td>{d.report_completed_by}</td>
              <td className="label">Date of Report</td>
              <td>{fmtDate(d.date_of_report)}</td>
            </tr>
            <tr>
              <td className="label">Incident Date & Time</td>
              <td>{fmtDateTime(d.incident_date_time)}</td>
              <td className="label">Place of Incident</td>
              <td>{d.place_of_incident}</td>
            </tr>
          </tbody>
        </table>

        {/* Clients */}
        <span className="section-title">Clients Involved</span>
        <table className="member-table">
          <thead>
            <tr>
              <th style={{ width: "10%" }}>S.No</th>
              <th>Name</th>
            </tr>
          </thead>
          <tbody>
            {d.clients?.length > 0 ? (
              d.clients.map((c, i) => (
                <tr key={i}>
                  <td style={{ textAlign: "center" }}>{i + 1}</td>
                  <td>{c.name}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} style={{ textAlign: "center", fontStyle: "italic" }}>
                  No clients recorded.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Staff */}
        <span className="section-title">Staff Members Involved</span>
        <table className="member-table">
          <thead>
            <tr>
              <th style={{ width: "10%" }}>S.No</th>
              <th>Name</th>
            </tr>
          </thead>
          <tbody>
            {d.staff?.length > 0 ? (
              d.staff.map((s, i) => (
                <tr key={i}>
                  <td style={{ textAlign: "center" }}>{i + 1}</td>
                  <td>{s.name}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} style={{ textAlign: "center", fontStyle: "italic" }}>
                  No staff members recorded.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Nature of Incident */}
        <span className="section-title">Nature of Incident</span>
        <table className="info-table">
          <tbody>
            {natureOptions.map((option, i) => (
              <tr key={i}>
                <td>
                  {option === "Other"
                    ? `Other: ${d.other_nature || ""}`
                    : option}
                </td>
                <td style={{ width: "10%", textAlign: "center", fontWeight: "bold" }}>
                  {d.nature_of_incident === option ? "✓" : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Summary */}
        <span className="section-title">Summary of Events</span>
        <div className="text-box">{d.summary}</div>

        <span className="section-title">Action Taken</span>
        <div className="text-box">{d.action_taken}</div>

        <span className="section-title">Other Witnesses</span>
        <div className="text-box">{d.other_witnesses}</div>

        <span className="section-title">Debriefing Details</span>
        <div className="text-box">{d.debriefing_details}</div>

        <span className="section-title">Other Comments</span>
        <div className="text-box">{d.other_comments}</div>

        {/* Director Section */}
        <span className="section-title">Director Information</span>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Director Name</td>
              <td>{d.director_name}</td>
              <td className="label">Date & Time Notified</td>
              <td>{fmtDateTime(d.director_date_time)}</td>
            </tr>
          </tbody>
        </table>

        {/* Follow Up */}
        <span className="section-title">Follow-Up Action</span>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Necessary?</td>
              <td>{d.follow_up_necessary}</td>
            </tr>
            {d.follow_up_necessary === "Yes" && (
              <tr>
                <td className="label">Details</td>
                <td>{d.follow_up_necessary_details}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Trustees */}
        <span className="section-title">Action Reported to Trustees</span>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Reported?</td>
              <td>{d.action_reported_to_trustees}</td>
            </tr>
            {d.action_reported_to_trustees === "Yes" && (
              <tr>
                <td className="label">Date</td>
              <td>{fmtDate(d.trustees_date)}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Signatures */}
        <span className="section-title">Staff Signatures</span>
        <table className="member-table">
          <thead>
            <tr>
              <th style={{ width: "10%" }}>S.No</th>
              <th style={{ width: "45%" }}>Name</th>
              <th style={{ width: "45%" }}>Signature</th>
            </tr>
          </thead>
          <tbody>
            {d.signatures?.length > 0 ? (
              d.signatures.map((sg, i) => (
                <tr key={i}>
                  <td style={{ textAlign: "center" }}>{i + 1}</td>
                  <td>{sg.name}</td>
                  <td></td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} style={{ textAlign: "center", fontStyle: "italic" }}>
                  No staff signatures recorded.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="footer">
          <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan</p>
          <p>Ph: 021-34546364-65-66</p>
          <p>
            <strong>A Project of “CARVAN OF LIFE PAKISTAN TRUST”</strong>
          </p>
        </div>
      </div>
    </>
  );
}
